import React, { Component } from "react";
import { observer } from "mobx-react";

import { countBy } from "@formant/core/common/countBy";
import { defined } from "@formant/core/common/defined";
import { AccessLevel } from "@formant/core/model/AccessLevel";
import { accessLevels } from "@formant/core/model/accessLevels";
import { ITeam } from "@formant/core/model/ITeam";

import { roleStore, routerStore, teamStore } from "../../../stores";
import { localSettingObservable } from "../../../utils/localSettingObservable";
import { Avatar } from "../../common/Avatar";
import {
    DataTable,
    defaultSimpleSortState,
    ISimpleSortState,
} from "../../common/DataTable";
import { IDataTableColumn } from "../../common/DataTable/IDataTableColumn";
import { Help } from "../../common/Help";
import { Scrollable } from "../../common/Scrollable";
import {
    applySimpleFilter,
    emptySimpleFilterState,
    ISimpleFilterState,
    SimpleFilter,
    tagDimensions,
} from "../../common/SimpleFilter";
import { StandardButton } from "../../common/StandardButton";
import { IStatHeaderGroup, StatsHeader } from "../../common/StatsHeader";
import { Tags } from "../../common/Tags";
import { RowDate } from "../RowDate";
import { RowTitle } from "../RowTitle";
import { SettingsPage } from "../SettingsPage";
import { Actions } from "./Actions";
import * as styles from "./index.scss";

@observer
export class Teams extends Component {
    @localSettingObservable("settings-teams-filter")
    private filter: ISimpleFilterState = emptySimpleFilterState;

    @localSettingObservable("settings-teams-sort")
    private sort: ISimpleSortState = defaultSimpleSortState;

    public render() {
        const roles = roleStore.list() || [];
        if (!roles.length) {
            return false;
        }

        const teams = teamStore.listAll() || [];
        const countByRole = countBy(teams || [], _ => _.roleId);
        const { routeTo } = routerStore;

        const columns: IDataTableColumn<ITeam>[] = [
            {
                header: "Name",
                row: _ => (
                    <RowTitle
                        title={_.name}
                        icon={<Avatar initials={_.name} size="small" />}
                    />
                ),
            },
            {
                header: "Role",
                row: _ =>
                    `${defined(
                        roles.find(role => role.id === _.roleId)?.name,
                    )}`,
                sortBy: _ =>
                    `${defined(
                        roles.find(role => role.id === _.roleId)?.name,
                    )}`,
                className: styles.role,
            },
            {
                header: "Tags",
                row: _ => <Tags tags={_.tags || {}} />,
                hideBelow: "lg",
            },
            {
                header: "Created",
                row: _ => <RowDate date={_.createdAt} />,
                sortBy: _ => _.createdAt,
                hideBelow: "xl",
            },
            {
                header: "",
                row: _ => <Actions team={_} />,
            },
        ];

        const statHeaderGroups: IStatHeaderGroup[] = roles
            .filter(_ => accessLevels.includes(_.name as AccessLevel))
            .map(role => ({
                subtotalQuantity: countByRole?.get(defined(role.id)) || 0,
                subtotalLabel: role.name,
            }));

        const { filter, onChangeFilter, sort, onChangeSort } = this;

        const dimensions = [...tagDimensions(teams || [], _ => _?.tags || {})];

        const filteredTeams = applySimpleFilter(
            teams || [],
            dimensions,
            this.getTeamText,
            filter,
        );

        return (
            <SettingsPage
                title="Teams"
                className={styles.teams}
                extra={
                    <Help
                        text="A team is a member of your organization. You can view, delete, or edit (including changing role between Viewer, Operator and Admin) teams, as well as invite new teams. An invited team will need to respond to an email invitation in order to join the Formant organization."
                        to="https://docs.formant.io/docs/users"
                    />
                }
                actions={
                    <>
                        <StandardButton to={routeTo.createTeam()}>
                            Add team
                        </StandardButton>
                    </>
                }
            >
                <StatsHeader
                    icon="user"
                    loading={!teams}
                    totalQuantity={teams?.length || 0}
                    totalLabel="Teams"
                    groups={statHeaderGroups}
                />
                <SimpleFilter
                    dimensions={dimensions}
                    rows={teams || []}
                    filter={filter}
                    onChangeFilter={onChangeFilter}
                />
                <Scrollable>
                    <DataTable
                        rows={filteredTeams || []}
                        columns={columns}
                        loading={teams === undefined}
                        noResultsText="No teams found"
                        onRowClicked={this.onEdit}
                        className={styles.table}
                        sort={sort}
                        onChangeSort={onChangeSort}
                    />
                </Scrollable>
            </SettingsPage>
        );
    }

    private getTeamText = (team: ITeam) => {
        return team.name;
    };

    private onChangeFilter = (filter: ISimpleFilterState) => {
        this.filter = filter;
    };

    private onChangeSort = (sort: ISimpleSortState) => {
        this.sort = sort;
    };

    private onEdit = async (team: ITeam) => {
        const { navigate, routeTo } = routerStore;
        await navigate(
            routeTo.editTeam(
                { id: defined(team.id) },
                { editing: true, fromSettings: true },
            ),
        );
    };
}
